import { VersionScope } from '../types/snackableCourse';
import {
  APPLIANCE_OFFER_TYPES,
  HYBRID_OFFER_TYPES,
  SAAS_OFFER_TYPES,
  normalizeOfferType,
} from './productRelationshipUtils';

export const VIEW_ALL_VERSION_VALUE = '__all__';

export const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === 'object' && value !== null && 'id' in value) {
    return toNumberOrNull((value as { id?: unknown }).id);
  }

  return null;
};

export const getProductVersionsFromProduct = (product: any): any[] => {
  const versions =
    product?.version ||
    product?.versions ||
    product?.product_version ||
    [];

  return Array.isArray(versions) ? versions.filter(Boolean) : [];
};

export const getVersionLabel = (version: any): string => {
  return String(version?.version || version?.name || version?.id || '').trim();
};

export const sortVersionsForDisplay = (versions: any[]) => {
  return [...(versions || [])].sort((a, b) => {
    const aReleased = a?.released ? new Date(a.released).getTime() : 0;
    const bReleased = b?.released ? new Date(b.released).getTime() : 0;

    if (aReleased !== bReleased) {
      return bReleased - aReleased;
    }

    return String(b?.version ?? '').localeCompare(String(a?.version ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

export const getRecommendedVersion = (
  versions: any[],
  recommendedVersion: unknown
): any | null => {
  const recommended = String(recommendedVersion ?? '').trim();

  if (!recommended) return null;

  return (
    versions.find((version) => String(version?.version ?? '').trim() === recommended) ||
    versions.find((version) => String(version?.name ?? '').trim() === recommended) ||
    versions.find((version) => String(version?.id ?? '').trim() === recommended) ||
    null
  );
};

export const isVersionScopedOfferType = (offerType: unknown): boolean => {
  const normalized = normalizeOfferType(offerType);
  return APPLIANCE_OFFER_TYPES.has(normalized);
};

export const isSaasOfferType = (offerType: unknown): boolean => {
  const normalized = normalizeOfferType(offerType);
  return SAAS_OFFER_TYPES.has(normalized);
};

export const getDeploymentScopeForOfferType = (offerType: unknown): string => {
  const normalized = normalizeOfferType(offerType);

  if (SAAS_OFFER_TYPES.has(normalized)) return 'saas';
  if (HYBRID_OFFER_TYPES.has(normalized)) return 'hybrid';
  if (APPLIANCE_OFFER_TYPES.has(normalized)) return 'appliance';

  return 'general';
};

export const hasMeaningfulVersionScope = (versionScope?: VersionScope | null): boolean => {
  return Boolean(
    versionScope?.product_id ||
    versionScope?.product_version_id ||
    versionScope?.product_license_id ||
    versionScope?.doc_version ||
    versionScope?.deployment_scope ||
    versionScope?.generation_scope
  );
};

export const buildRecommendedVersionScope = ({
                                               product,
                                               productId,
                                               versionScope,
                                             }: {
  product: any;
  productId: number | string | null | undefined;
  versionScope?: VersionScope | null;
}): VersionScope | null => {
  /**
   * Explicit selected version wins.
   */
  if (
    versionScope?.product_version_id ||
    versionScope?.doc_version ||
    versionScope?.deployment_scope ||
    versionScope?.generation_scope
  ) {
    return {
      ...versionScope,
      product_id: versionScope.product_id ?? toNumberOrNull(productId) ?? toNumberOrNull(product?.id),
      product_name: versionScope.product_name ?? product?.name ?? null,
    };
  }

  const offerType = normalizeOfferType(product?.offer_type);
  const resolvedProductId = toNumberOrNull(productId) ?? toNumberOrNull(product?.id);

  /**
   * SaaS products are treated as a single current service state.
   */
  if (isSaasOfferType(offerType)) {
    return {
      product_id: resolvedProductId,
      product_name: product?.name ?? null,
      product_version_id: null,
      product_version_name: null,
      product_license_id: null,
      product_license_name: null,
      doc_version: 'saas',
      deployment_scope: 'saas',
      generation_scope: 'product',
      version_source: 'saas',
    };
  }

  /**
   * Non-versioned products should remain generic.
   */
  if (!isVersionScopedOfferType(offerType)) {
    return null;
  }

  const versions = sortVersionsForDisplay(getProductVersionsFromProduct(product));

  if (!versions.length) {
    return {
      product_id: resolvedProductId,
      product_name: product?.name ?? null,
      product_version_id: null,
      product_version_name: null,
      product_license_id: null,
      product_license_name: null,
      doc_version: product?.recommended_version || null,
      deployment_scope: getDeploymentScopeForOfferType(offerType),
      generation_scope: 'version',
      version_source: 'recommended_version_missing_version_record',
    };
  }

  const recommended = getRecommendedVersion(versions, product?.recommended_version);
  const selectedVersion = recommended || versions[0];

  return {
    product_id: resolvedProductId,
    product_name: product?.name ?? null,
    product_version_id: toNumberOrNull(selectedVersion?.id),
    product_version_name: getVersionLabel(selectedVersion),
    product_license_id: null,
    product_license_name: null,
    doc_version: getVersionLabel(selectedVersion),
    deployment_scope: getDeploymentScopeForOfferType(offerType),
    generation_scope: 'version',
    version_source: recommended ? 'recommended_version' : 'latest_product_version',
  };
};